import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useState } from 'react'
import { Control, FieldErrors, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useMediaQuery } from 'usehooks-ts'

import {
  useCheckIsEmailUsedMutation,
  useCustomerProfileFieldsQuery,
  useUpdateCustomerMutation,
} from '@entities/User/api/profile.gql'
import { buildSchemaFromFields, groupProfileFields } from '@entities/User/helpers/profileFields'

import { useAuth } from '@features/auth'
import { useErrorInfoModal } from '@features/modal'

import BackButton from '@shared/ui/back-button'
import Button from '@shared/ui/button'
import flattenProfileInfo from '@shared/uttils/flattenProfileInfo'

import FormField from './FormField'
import { ProfileInfoForm } from './info'

import styles from './styles.module.scss'

import PencilIcon from '/icons/pencil.svg'

// Form type for profile edit (without acceptPolicy)
interface EditProfileForm {
  profileInfo: Record<string, string | boolean | null | undefined>
}

function SkeletonLoading () {
  return (
    <SkeletonTheme>
      <div style={{ minHeight: '800px' }}>
        <div>
          <Skeleton
            count={3}
            width={'100%'}
            height={56}
            style={{
              borderRadius: 6,
              marginBottom: 20,
            }}
          />
        </div>
        <div>
          <Skeleton
            count={3}
            width={'100%'}
            height={56}
            style={{
              borderRadius: 6,
              marginBottom: 20,
            }}
          />
        </div>
      </div>
    </SkeletonTheme>
  )
}

export default function ProfileEdit () {
  const { openErrorInfoModal } = useErrorInfoModal()
  const { t } = useTranslation()
  const { t: tc } = useTranslation('common')
  const { t: tu } = useTranslation('user')
  const { t: te } = useTranslation('errors')
  const {
    data,
    loading: loadingProfileFields,
    refetch: refetchProfileFields,
  } = useCustomerProfileFieldsQuery()
  const [ fetchCheckEmail, { loading: checkEmailLoading } ] =
    useCheckIsEmailUsedMutation()
  const [ isCorrectEmail, setIsCorrectEmail ] = useState(true)
  const [ originalEmail, setOriginalEmail ] = useState<string | null>(null)
  const [ updateCustomer, { loading: updateCustomerLoading } ] =
    useUpdateCustomerMutation()
  const [ isEdit, setIsEdit ] = useState<boolean>(false)
  const { refetchUser, user } = useAuth()
  const isTablet = useMediaQuery('(max-width: 991px)')

  // Group and normalize fields
  const { fieldsByGroup, normalizedFields } = useMemo(
    () => {
      const fields = data?.customerProfileFields?.filter((f): f is NonNullable<typeof f> => f !== null) ?? []
      return groupProfileFields(fields, false)
    },
    [ data?.customerProfileFields ],
  )

  // Build dynamic Zod schema
  const schema = useMemo(() => {
    if (normalizedFields.length === 0) return null
    
    return buildSchemaFromFields(normalizedFields, { includeAcceptPolicy: false })
  }, [ normalizedFields ])

  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<EditProfileForm>({
    resolver: schema ? zodResolver(schema) : undefined,
    defaultValues: {
      profileInfo: {},
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  const onSubmit = async (dto: EditProfileForm) => {
    try {
      const answers = flattenProfileInfo(dto.profileInfo).map((i) => {
        // Convert normalized fieldName back to original format (__ -> .)
        const originalFieldName = i.fieldName.split('__').join('.')
        const field = normalizedFields?.find((f) => f?.fieldName === i.fieldName)
        
        return {
          fieldName: originalFieldName,
          id: field?.id ?? '',
          value: String(i.value),
        }
      })

      await updateCustomer({
        variables: {
          answers,
        },
      })

      await refetchProfileFields()

      await refetchUser()

      setIsEdit(false)
    }
    catch (error) {
      console.error('[ProfileEdit] submit error', error)
      openErrorInfoModal({
        description: te('edit_profile'),
      })
      console.error('Customer profile edit error:', error)
    }
  }

  const watchedEmail = watch('profileInfo.email')

  const handleCheckEmail = async () => {
    if (!watchedEmail) return

    if (watchedEmail === originalEmail) {
      setIsCorrectEmail(true)
      return
    }

    try {
      const res = await fetchCheckEmail({
        variables: { email: watchedEmail as string },
      })

      setIsCorrectEmail(!res.data?.checkIsEmailUsed)
    }
    catch (_error) {
      setIsCorrectEmail(true)
      openErrorInfoModal({
        description: te('check_email'),
      })
    }
  }

  useEffect(() => {
    if (!normalizedFields?.length) {
      return
    }

    const defaultValues: Record<string, unknown> = {}
    let emailValue = ''

    normalizedFields.forEach((field) => {
      if (field?.fieldName && field?.answer !== null && field?.answer !== undefined) {
        if (field.type === 'boolean') {
          defaultValues[field.fieldName] = field.answer === 'true'
        }
        else if (field.type === 'number') {
          defaultValues[field.fieldName] = Number(field.answer)
        }
        else if (field.type === 'select') {
          // Convert to string for select fields (fixedAnswers values are strings)
          defaultValues[field.fieldName] = String(field.answer)
        }
        else if (field.type === 'string' || field.type === 'email' || field.type === 'date') {
          // Convert to string for text-based fields (backend may send numbers)
          defaultValues[field.fieldName] = String(field.answer)
        }
        else {
          defaultValues[field.fieldName] = field.answer
        }

        if (field.type === 'email') {
          emailValue = String(field.answer)
        }
      }
      else if (field?.defaultValue) {
        if (field.type === 'boolean') {
          defaultValues[field.fieldName] = field.defaultValue === 'true'
        }
        else if (field.type === 'number') {
          defaultValues[field.fieldName] = Number(field.defaultValue)
        }
        else if (field.type === 'select') {
          defaultValues[field.fieldName] = String(field.defaultValue)
        }
        else if (field.type === 'string' || field.type === 'email' || field.type === 'date') {
          defaultValues[field.fieldName] = String(field.defaultValue)
        }
        else {
          defaultValues[field.fieldName] = field.defaultValue
        }
      }
      else {
        // Set appropriate default values by type
        if (field.type === 'boolean') {
          defaultValues[field.fieldName] = false
        }
        else if (field.type === 'number') {
          defaultValues[field.fieldName] = 0
        }
        else {
          defaultValues[field.fieldName] = ''
        }
      }
    })

    setOriginalEmail(emailValue)

    const profileInfo = defaultValues as Record<
      string,
      string | boolean | null | undefined
    >
    
    reset({ profileInfo }, { keepDefaultValues: true })
  }, [ normalizedFields, reset ])

  useEffect(() => {
    if (watchedEmail && !errors.profileInfo?.email?.message) {
      handleCheckEmail()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ watchedEmail ])

  useEffect(() => {
    refetchProfileFields()
  }, [ refetchProfileFields ])

  const LOADING = useMemo(() => {
    return loadingProfileFields || updateCustomerLoading
  }, [ loadingProfileFields, updateCustomerLoading ])

  return (
    <div className={styles.edit}>
      <div className={styles.top}>
        {isTablet && <BackButton />}

        <h1 className={styles.edit__title}>{tu('profileInfo.title2')}</h1>
        {!isEdit && (
          <Button
            className={styles.edit__button}
            theme="grey"
            onClick={() => {
              setIsEdit(true)
            }}
          >
            <img src={PencilIcon} alt="" />
          </Button>
        )}
      </div>

      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        {/* Phone number section */}
        {user?.phone && (
          <div className={styles.form__section}>
            <h3 className={styles.form__sectionTitle}>{tu('phone')}</h3>
            <div className={styles.form__phoneDisplay}>
              +{user.phone}
            </div>
            <p className={styles.form__note}>
              {tu('phoneChangeNote')}
            </p>
          </div>
        )}

        <div className={styles.form__grid}>
          {LOADING && <SkeletonLoading />}

          {!LOADING && Object.entries(fieldsByGroup).length > 0 && (
            Object.entries(fieldsByGroup).map(([ group, fields ]) => (
              <div key={group} className={styles.form__group}>
                {group && group !== 'default' && (
                  <h3 className={styles.form__groupTitle}>
                    {t(group)}
                  </h3>
                )}
                {fields.map((field) => (
                  <FormField
                    key={field.id}
                    field={field}
                    control={control as unknown as Control<ProfileInfoForm>}
                    errors={errors as unknown as FieldErrors<ProfileInfoForm>}
                    disabled={!isEdit}
                    isCorrectEmail={isCorrectEmail}
                    originalEmail={originalEmail}
                  />
                ))}
              </div>
            ))
          )}
        </div>

        {isEdit && (
          <Button
            className={styles.button}
            type="submit"
            size="large"
            disabled={
              !isDirty ||
              loadingProfileFields ||
              checkEmailLoading ||
              (!isCorrectEmail && watchedEmail !== originalEmail)
            }
          >
            {tc('save')}
          </Button>
        )}
      </form>
    </div>
  )
}
